import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
  IconButton,
  Tooltip,
  Autocomplete
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { agencyService } from '../services/agencyService';
import { getErrorMessage } from '../utils/errorMessages';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, ManageAccounts as ManageAccountsIcon, Info as InfoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import UserRoleManager from '../components/UserRoleManager';
import { hasAdminAccess, hasRole } from '../utils/roleUtils';

const normalizeDomain = (value?: string) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  // If this already looks like an email domain, strip any leading www.
  if (!trimmed.includes('/') && !trimmed.startsWith('http')) {
    return trimmed.toLowerCase().startsWith('www.')
      ? trimmed.toLowerCase().slice(4)
      : trimmed.toLowerCase();
  }

  let url = trimmed;
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  } catch {
    return '';
  }
};

const getEmailDomain = (email?: string) => {
  if (!email) return '';
  const domain = email.split('@')[1]?.trim().toLowerCase() || '';
  return domain.startsWith('www.') ? domain.slice(4) : domain;
};

const matchesAgencyDomain = (emailDomain: string, allowedDomain: string) => {
  if (!emailDomain || !allowedDomain) return false;
  return emailDomain === allowedDomain || emailDomain.endsWith(`.${allowedDomain}`);
};

interface TeamMember {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: 'admin' | 'user'; // New role field for access control
  position?: string; // Job position
  primaryRole: 'admin' | 'sales_manager' | 'sales_agent' | 'rental_agent' | 'sales_assistant' | 'rental_assistant' | 'property_manager' | 'photographer' | 'styler'; // Legacy
  roles: string[]; // Array of all active roles (legacy)
  phone: string;
  isEmailVerified: boolean;
  isActive: boolean;
  createdAt: string;
}

// Primary role options (for workflow/property access); must match backend valid roles
const PRIMARY_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'sales_agent', label: 'Sales Agent' },
  { value: 'rental_agent', label: 'Rental Agent' },
  { value: 'sales_assistant', label: 'Sales Assistant' },
  { value: 'rental_assistant', label: 'Rental Assistant' },
  { value: 'property_manager', label: 'Property Manager' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'styler', label: 'Styler' },
];

// Default position titles (sorted alphabetically)
const DEFAULT_POSITIONS = [
  'Administrator',
  'Business Development Manager',
  'Director',
  'Marketing Manager',
  'Office Manager',
  'Photographer',
  'Principal',
  'Property Manager',
  'Rental Agent',
  'Rental Assistant',
  'Sales Agent',
  'Sales Assistant',
  'Sales Manager',
  'Styler',
];

const Team = () => {
  const { agency, user } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const canInviteMembers = !['free', 'agency free', 'individual', 'elite individual'].includes(planName); // Individual: 1 seat only, no team
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const [formErrors, setFormErrors] = useState<{ email?: string; position?: string }>({});
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'user' as 'admin' | 'user',
    position: '',
  });

  // Add state for confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    member: null as TeamMember | null,
    newStatus: false
  });

  // Add state for delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    member: null as TeamMember | null
  });

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editRowData, setEditRowData] = useState<{
    role?: 'admin' | 'user';
    position?: string;
    primaryRole:
      | 'admin'
      | 'sales_manager'
      | 'sales_agent'
      | 'rental_agent'
      | 'sales_assistant'
      | 'rental_assistant'
      | 'property_manager'
      | 'photographer'
      | 'styler';
    roles: string[];
    isActive: boolean;
    phone: string;
  } | null>(null);
  const [roleManagerOpen, setRoleManagerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);

  const allowedDomains = useMemo(() => {
    const domains = new Set<string>();
    if (agency?.website) {
      const websiteDomain = normalizeDomain(agency.website);
      if (websiteDomain) domains.add(websiteDomain);
    }
    if (agency?.email) {
      const agencyEmailDomain = getEmailDomain(agency.email);
      if (agencyEmailDomain) domains.add(agencyEmailDomain);
    }
    return Array.from(domains);
  }, [agency?.website, agency?.email]);

  const validateEmailDomain = useCallback((email: string) => {
    if (!email) return '';
    const emailDomain = getEmailDomain(email);

    if (!emailDomain) {
      return 'Please enter a valid agency email.';
    }

    if (allowedDomains.length === 0) {
      return '';
    }

    const hasValidDomain = allowedDomains.some((domain) =>
      matchesAgencyDomain(emailDomain, domain)
    );

    if (!hasValidDomain) {
      const domainList = allowedDomains.join(', ');
      return `Invites must use your agency domain (${domainList}).`;
    }

    return '';
  }, [allowedDomains]);

  useEffect(() => {
    fetchTeamMembers();
  }, [agency?.slug]);

  const fetchTeamMembers = async () => {
    if (!agency?.slug) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/agencies/${agency.slug}/users`);
      setTeamMembers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agency?.slug) return;

    const emailError = validateEmailDomain(formData.email);
    if (emailError) {
      setFormErrors(prev => ({ ...prev, email: emailError }));
      return;
    }

    const positionTrimmed = (formData.position || '').trim();
    if (!positionTrimmed) {
      setFormErrors(prev => ({ ...prev, position: 'Position is required' }));
      return;
    }

    // Check if user is trying to invite with admin role and current user is not admin
    if (formData.role === 'admin' && !hasAdminAccess(user)) {
      setError('Only admins can invite users with admin role.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post(`/agencies/${agency.slug}/users`, formData);
      setSuccess('User invited successfully. They will receive an email to set up their account.');
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'user',
        position: '',
      });
      setFormErrors({});
      setOpenModal(false);
      fetchTeamMembers(); // Refresh the list
    } catch (err: any) {
      const errorData = err.response?.data;
      
      // Check if this is a seat limit error
      if (errorData?.upgradeRequired) {
        setError(
          `${errorData.message || 'Seat limit reached'}. ` +
          `Go to Settings → Manage Subscription to upgrade your plan.`
        );
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'email') {
      const errorMessage = validateEmailDomain(value);
      setFormErrors(prev => ({
        ...prev,
        email: errorMessage || undefined
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getRoleDisplayName = (role: string | undefined) => {
    if (!role) return 'Unknown Role';
    
    const roleNames: Record<string, string> = {
      'admin': 'Admin',
      'user': 'User',
      'app_admin': 'App Admin',
      'sales_manager': 'Sales Manager',
      'sales_agent': 'Sales Agent',
      'rental_agent': 'Rental Agent',
      'sales_assistant': 'Sales Assistant',
      'rental_assistant': 'Rental Assistant',
      'property_manager': 'Property Manager',
      'photographer': 'Photographer',
      'styler': 'Styler'
    };
    return roleNames[role] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getRoleColor = (role: string | undefined): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    if (!role) return 'default';
    
    // New role system colors
    if (role === 'admin') return 'primary';
    if (role === 'user') return 'default';
    
    // Legacy role colors
    const roleColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
      'sales_manager': 'secondary',
      'sales_agent': 'success',
      'rental_agent': 'success',
      'sales_assistant': 'default',
      'rental_assistant': 'default',
      'property_manager': 'warning',
      'photographer': 'default',
      'styler': 'default'
    };
    return roleColors[role] || 'default';
  };

  const handleActiveStatusChange = async (userId: string, isActive: boolean) => {
    if (!agency?.slug) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await api.patch(`/agencies/${agency.slug}/users/${userId}/active`, { isActive });
      setSuccess(`User has been ${isActive ? 'activated' : 'deactivated'}.`);
      fetchTeamMembers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user status');
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, member: null, newStatus: false });
    }
  };

  const handleDeleteTeamMember = async (userId: string) => {
    if (!agency?.slug) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await agencyService.deleteTeamMember(agency.slug, userId);
      setSuccess('Team member deleted successfully.');
      fetchTeamMembers(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete team member');
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, member: null });
    }
  };

  const startEditRow = async (member: TeamMember) => {
    setEditingUserId(member._id);
    // Use member.roles from the list (User document) so the Extra roles multi-select matches what the table displays
    setEditRowData({
      role: member.role || (member.primaryRole === 'admin' || member.primaryRole === 'app_admin' ? 'admin' : 'user'),
      position: member.position,
      primaryRole: member.primaryRole,
      roles: member.roles || [],
      isActive: member.isActive,
      phone: member.phone,
    });
  };

  const openRoleManager = (member: TeamMember) => {
    setSelectedUser(member);
    setRoleManagerOpen(true);
  };

  const closeRoleManager = () => {
    setRoleManagerOpen(false);
    setSelectedUser(null);
  };

  const handleRoleUpdate = () => {
    fetchTeamMembers(); // Refresh the team members list
  };
  const cancelEditRow = () => {
    setEditingUserId(null);
    setEditRowData(null);
  };
  const saveEditRow = async (member: TeamMember) => {
    if (!agency?.slug || !editRowData) {
      return;
    }
    
    // Check if user is trying to assign admin role and current user is not admin
    if (editRowData.role === 'admin' && !hasAdminAccess(user)) {
      setError('Only admins can assign admin role to other users.');
      return;
    }

    const positionTrimmed = (editRowData.position ?? member.position ?? '').trim();
    if (!positionTrimmed) {
      setError('Position is required. Please set a job position for this team member.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Update role if changed
      const currentRole = member.role || (member.primaryRole === 'admin' || member.primaryRole === 'app_admin' ? 'admin' : 'user');
      if (editRowData.role && editRowData.role !== currentRole) {
        await agencyService.updateUserRole(agency.slug, member._id, { role: editRowData.role });
      }
      
      if (editRowData.position !== undefined && editRowData.position !== member.position) {
        await agencyService.updateUserRole(agency.slug, member._id, { position: editRowData.position });
      }
      
      if (member.primaryRole !== editRowData.primaryRole) {
        await agencyService.updatePrimaryRole(agency.slug, member._id, { primaryRole: editRowData.primaryRole });
      }

      // Always sync roles so backend (User.roles + UserRole collection) matches the form
      const currentUserRoles = await agencyService.getUserRoles(agency.slug, member._id);
      const currentRoleList = (currentUserRoles.data?.roles ?? []).map((r: any) => (typeof r === 'string' ? r : r?.role)).filter(Boolean);
      const targetRoles = editRowData.roles ?? [];
      for (const r of currentRoleList) {
        if (!targetRoles.includes(r)) {
          await agencyService.removeUserRole(agency.slug, member._id, r);
        }
      }
      for (const r of targetRoles) {
        if (!currentRoleList.includes(r)) {
          await agencyService.addUserRole(agency.slug, member._id, {
            role: r as 'admin' | 'sales_manager' | 'sales_agent' | 'rental_agent' | 'sales_assistant' | 'rental_assistant' | 'property_manager' | 'photographer' | 'styler',
          });
        }
      }
      
      if (member.isActive !== editRowData.isActive) {
        await agencyService.updateUserActiveStatus(agency.slug, member._id, { isActive: editRowData.isActive });
      }
      if (member.phone !== editRowData.phone) {
        await api.patch(`/agencies/${agency.slug}/users/${member._id}/phone`, { phone: editRowData.phone });
      }
      setSuccess('User updated successfully.');
      fetchTeamMembers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
      setEditingUserId(null);
      setEditRowData(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Team Members
        </Typography>
        <Tooltip
          title={
            canInviteMembers
              ? ''
              : 'Upgrade to the Agency Premium plan to invite additional team members.'
          }
          placement="left"
          arrow
          disableHoverListener={canInviteMembers}
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={() => canInviteMembers && setOpenModal(true)}
              disabled={!canInviteMembers}
              startIcon={!canInviteMembers ? <InfoIcon /> : undefined}
            >
              Invite Team Member
            </Button>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Primary role</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Extra roles</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {canInviteMembers
                      ? 'No team members found. Invite your first team member to get started.'
                      : ['individual', 'elite individual'].includes(planName)
                        ? 'Elite Individual plan does not include team management.'
                        : 'The Free plan is limited to a single user. Upgrade to invite additional team members.'}
                  </TableCell>
                </TableRow>
              ) : (
                teamMembers.map((member) => {
                  const isEditing = editingUserId === member._id;
                  return (
                    <TableRow key={member._id}>
                      <TableCell>{member.firstName} {member.lastName}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                              value={editRowData?.role || member.role || 'user'}
                              onChange={e => setEditRowData(d => d ? { 
                                ...d, 
                                role: e.target.value as 'admin' | 'user'
                              } : d)}
                              disabled={loading}
                              label="Role"
                            >
                              <MenuItem value="user">User</MenuItem>
                              {hasAdminAccess(user) && <MenuItem value="admin">Admin</MenuItem>}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip 
                            label={getRoleDisplayName(member.role || member.primaryRole)} 
                            color={getRoleColor(member.role || member.primaryRole)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <FormControl size="small" variant="outlined" sx={{ minWidth: 140 }}>
                            <InputLabel>Primary role</InputLabel>
                            <Select
                              value={editRowData?.primaryRole ?? member.primaryRole ?? 'sales_agent'}
                              onChange={(e) => {
                                const newPrimary = e.target.value as string;
                                setEditRowData((d) =>
                                  d
                                    ? {
                                        ...d,
                                        primaryRole: newPrimary as typeof d.primaryRole,
                                        roles: [...new Set([newPrimary, ...d.roles.filter((r) => r !== newPrimary)])],
                                      }
                                    : d
                                );
                              }}
                              disabled={loading}
                              label="Primary role"
                            >
                              {PRIMARY_ROLE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={getRoleDisplayName(member.primaryRole)}
                            color={getRoleColor(member.primaryRole)}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <Autocomplete
                            freeSolo
                            options={DEFAULT_POSITIONS}
                            value={editRowData?.position !== undefined ? editRowData.position : (member.position || '')}
                            onChange={(event, newValue) => {
                              setEditRowData(d => d ? { 
                                ...d, 
                                position: typeof newValue === 'string' ? newValue : newValue || '' 
                              } : d);
                            }}
                            onInputChange={(event, newInputValue) => {
                              setEditRowData(d => d ? { 
                                ...d, 
                                position: newInputValue 
                              } : d);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select or type"
                                variant="outlined"
                              />
                            )}
                            disabled={loading}
                            slotProps={{
                              popper: {
                                sx: {
                                  '& .MuiAutocomplete-paper': {
                                    minWidth: '350px !important',
                                    width: 'auto !important',
                                  },
                                  '& .MuiAutocomplete-listbox': {
                                    minWidth: '350px',
                                    width: '100%',
                                  },
                                  '& .MuiAutocomplete-option': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                    padding: '8px 16px',
                                  },
                                },
                              },
                            }}
                          />
                        ) : (
                          <Typography variant="body2">
                            {member.position || '-'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <FormControl size="small" variant="outlined" sx={{ minWidth: 160 }}>
                            <InputLabel>Extra roles</InputLabel>
                            <Select
                              multiple
                              value={
                                (editRowData?.roles ?? member.roles ?? []).filter(
                                  (r: string) => r !== (editRowData?.primaryRole ?? member.primaryRole)
                                )
                              }
                              onChange={(e) => {
                                const selected = e.target.value as string[];
                                const primary = editRowData?.primaryRole ?? member.primaryRole;
                                setEditRowData((d) =>
                                  d ? { ...d, roles: primary ? [primary, ...selected] : selected } : d
                                );
                              }}
                              disabled={loading}
                              label="Extra roles"
                              renderValue={(selected: string[]) =>
                                selected.length === 0 ? '—' : selected.map(getRoleDisplayName).join(', ')
                              }
                            >
                              {PRIMARY_ROLE_OPTIONS.filter(
                                (opt) => opt.value !== (editRowData?.primaryRole ?? member.primaryRole)
                              ).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          (() => {
                            const allRoles = member.roles || [];
                            const extraRoles = allRoles.filter((r: string) => r !== member.primaryRole);
                            if (extraRoles.length === 0)
                              return <Typography variant="body2" color="text.secondary">—</Typography>;
                            return (
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {extraRoles.map((r: string) => (
                                  <Chip
                                    key={r}
                                    label={getRoleDisplayName(r)}
                                    color={getRoleColor(r)}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            );
                          })()
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            value={editRowData?.phone ?? member.phone}
                            onChange={e => setEditRowData(d => d ? { ...d, phone: e.target.value } : d)}
                            size="small"
                            variant="outlined"
                            disabled={loading}
                          />
                        ) : (
                          member.phone
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={member.isEmailVerified ? 'Verified' : 'Pending'} 
                          color={member.isEmailVerified ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isCurrentUser = (user?._id || (user as any)?.id) === member._id;
                          const isMemberAdmin = member.role === 'admin' || member.primaryRole === 'admin' || member.primaryRole === 'app_admin';
                          
                          // Admins can edit their own active status, but not other admins
                          if (isMemberAdmin && !isCurrentUser) {
                            return <Switch checked={member.isActive} disabled color="success" />;
                          }
                          
                          if (isEditing) {
                            return (
                              <Switch
                                checked={editRowData?.isActive ?? member.isActive}
                                onChange={e => setEditRowData(d => d ? { ...d, isActive: e.target.checked } : d)}
                                color="success"
                                disabled={loading}
                              />
                            );
                          }
                          
                          return <Switch checked={member.isActive} disabled color="success" />;
                        })()}
                      </TableCell>
                      <TableCell>{formatDate(member.createdAt)}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <>
                            <IconButton onClick={() => saveEditRow(member)} disabled={loading} color="success">
                              <SaveIcon />
                            </IconButton>
                            <IconButton onClick={cancelEditRow} disabled={loading} color="error">
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : (
                          <Box display="flex" gap={1}>
                            {(() => {
                              const isCurrentUser = (user?._id || (user as any)?.id) === member._id;
                              const isMemberAdmin = member.role === 'admin' || member.primaryRole === 'admin' || member.primaryRole === 'app_admin';
                              
                              // Admins can edit any team member (including other admins). Non-admins (e.g. sales_manager) can only edit non-admin sales agents/assistants.
                              const canEdit = hasAdminAccess(user) ||
                                            (!isMemberAdmin && (
                                              hasRole(user, 'sales_manager') && (member.primaryRole === 'sales_agent' || member.primaryRole === 'sales_assistant')
                                            ));
                              
                              return (
                                <>
                                  {canEdit && (
                                    <IconButton onClick={() => startEditRow(member)} disabled={loading} color="primary">
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                  {hasAdminAccess(user) && (
                                    <IconButton onClick={() => openRoleManager(member)} disabled={loading} color="secondary">
                                      <ManageAccountsIcon />
                                    </IconButton>
                                  )}
                                  {hasAdminAccess(user) && !isCurrentUser && (
                                    <Tooltip title="Remove team member">
                                      <IconButton 
                                        onClick={() => setDeleteDialog({ open: true, member })} 
                                        disabled={loading} 
                                        color="error"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, member: null, newStatus: false })}
      >
        <DialogTitle>
          {confirmDialog.newStatus ? 'Activate User' : 'Deactivate User'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.newStatus ? 'activate' : 'deactivate'} {confirmDialog.member?.firstName} {confirmDialog.member?.lastName}?
          </Typography>
          {!confirmDialog.newStatus && (
            <Typography color="warning.main" sx={{ mt: 2 }}>
              Deactivating a user will prevent them from accessing the system.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, member: null, newStatus: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.member) {
                handleActiveStatusChange(confirmDialog.member._id, confirmDialog.newStatus);
              }
            }}
            color={confirmDialog.newStatus ? 'success' : 'warning'}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Updating...' : confirmDialog.newStatus ? 'Activate' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, member: null })}
      >
        <DialogTitle>Delete Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteDialog.member?.firstName} {deleteDialog.member?.lastName} ({deleteDialog.member?.email})?
          </Typography>
          <Typography color="error.main" sx={{ mt: 2, fontWeight: 600 }}>
            This action cannot be undone. The team member will be permanently removed from the agency.
          </Typography>
          {deleteDialog.member?.isActive && deleteDialog.member?.isEmailVerified && (
            <Typography color="info.main" sx={{ mt: 1 }}>
              Removing this member will update your subscription seat count and may affect billing.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, member: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteDialog.member) {
                handleDeleteTeamMember(deleteDialog.member._id);
              }
            }}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleTextChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleTextChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleTextChange}
                  required
                  margin="normal"
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleTextChange}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleSelectChange}
                    label="Role"
                    required
                  >
                    <MenuItem value="user">User</MenuItem>
                    {hasAdminAccess(user) && <MenuItem value="admin">Admin</MenuItem>}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Role determines access control. Admin has full access, User has standard access.
                  </Typography>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={DEFAULT_POSITIONS}
                  value={formData.position}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      position: typeof newValue === 'string' ? newValue : newValue || ''
                    }));
                    if (formErrors.position) setFormErrors(prev => ({ ...prev, position: undefined }));
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      position: newInputValue
                    }));
                    if (formErrors.position) setFormErrors(prev => ({ ...prev, position: undefined }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      label="Position"
                      margin="normal"
                      placeholder="Select or type a position (e.g. Sales Agent, Property Manager)"
                      helperText={formErrors.position || 'Required: Select from common positions or type a custom position'}
                      error={Boolean(formErrors.position)}
                    />
                  )}
                  slotProps={{
                    popper: {
                      sx: {
                        '& .MuiAutocomplete-paper': {
                          minWidth: '350px !important',
                          width: 'auto !important',
                        },
                        '& .MuiAutocomplete-listbox': {
                          minWidth: '350px',
                          width: '100%',
                        },
                        '& .MuiAutocomplete-option': {
                          whiteSpace: 'nowrap',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          padding: '8px 16px',
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Invite User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* User Role Manager Dialog */}
      <UserRoleManager
        open={roleManagerOpen}
        onClose={closeRoleManager}
        user={selectedUser}
        agencySlug={agency?.slug || ''}
        onRoleUpdate={handleRoleUpdate}
      />
    </Container>
  );
};

export default Team; 